import { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, User } from 'react-feather';
import { SalesRep, SalesRepCreate } from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import {
  getSupplierSalesReps,
  createSalesRep,
  updateSalesRep,
  deleteSalesRep,
} from '../../../../../redux/services/supplierAdditionalDetailsService';
import { DillButton, DillToggle } from '../../../../general/Dill';
import { SalesRepFormModal } from './SalesRepFormModal';

interface SalesRepsSettingsSectionProps {
  supplierId: string;
}

export const SalesRepsSettingsSection = ({ supplierId }: SalesRepsSettingsSectionProps) => {
  const dispatch = useAppDispatch();
  const { salesReps, loadingList } = useAppSelector((state) => state.salesReps);
  const { supplierUsers } = useAppSelector((state) => state.supplier);

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingSalesRep, setEditingSalesRep] = useState<SalesRep | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (supplierId) {
      dispatch(getSupplierSalesReps({ supplierId }));
    }
  }, [supplierId, dispatch]);

  const isLoading = loadingList.includes('getSupplierSalesReps');

  const handleCreate = () => {
    setEditingSalesRep(null);
    setErrorMessage(null);
    setIsFormModalOpen(true);
  };

  const handleEdit = (salesRep: SalesRep) => {
    setEditingSalesRep(salesRep);
    setErrorMessage(null);
    setIsFormModalOpen(true);
  };

  const handleClose = () => {
    setIsFormModalOpen(false);
    setEditingSalesRep(null);
    setErrorMessage(null);
  };

  const extractErrorMessage = (payload: unknown): string | null => {
    if (payload && typeof payload === 'object' && 'errors' in (payload as any)) {
      const errors = (payload as any).errors;
      if (Array.isArray(errors) && errors[0]?.msg) return errors[0].msg as string;
    }
    return null;
  };

  const handleSubmit = async (data: SalesRepCreate) => {
    if (!supplierId) return;
    setErrorMessage(null);
    const result = editingSalesRep
      ? await dispatch(updateSalesRep({ supplierId, salesRepId: editingSalesRep.id, data }))
      : await dispatch(createSalesRep({ supplierId, data }));
    const payload = result.payload as { success?: boolean } | null;
    if (!payload || payload.success === false) {
      setErrorMessage(extractErrorMessage(payload) || 'Failed to save sales rep.');
      return;
    }
    handleClose();
  };

  const handleDelete = async (salesRepId: string) => {
    await dispatch(deleteSalesRep({ supplierId, salesRepId }));
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4 gap-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
            <User size={18} />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900 m-0">Sales Reps</p>
            <p className="text-xs text-gray-400 mt-0.5 m-0">
              Assign a sales rep to each customer on their profile, then pick "the customer's sales
              rep" as a Collection Sequence step's assignee.
            </p>
          </div>
        </div>
        <DillButton
          label="New sales rep"
          icon={<Plus size={14} />}
          onClick={handleCreate}
          height={31}
        />
      </div>

      {isLoading && salesReps.length === 0 && (
        <p className="text-xs text-gray-400 text-center py-8">Loading sales reps…</p>
      )}

      {!isLoading && salesReps.length === 0 && (
        <div className="bg-gray-50 border border-dashed border-gray-200 rounded-xl py-10 flex flex-col items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-300 flex items-center justify-center">
            <User size={18} />
          </div>
          <p className="text-sm text-gray-500 m-0 mt-1">No sales reps yet</p>
          <p className="text-xs text-gray-400 m-0">
            Add your team here, then assign one to each customer.
          </p>
        </div>
      )}

      <div className="flex flex-col gap-2.5">
        {salesReps.map((salesRep) => (
          <div
            key={salesRep.id}
            className="group bg-white border border-gray-100 rounded-xl p-4 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-blue-50 text-blue-600">
              <User size={18} />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-sm font-semibold text-gray-900 m-0 truncate">{salesRep.name}</p>
                {salesRep.userId && (
                  <span className="text-[10px] font-medium text-blue-700 bg-blue-50 rounded-full px-2 py-0.5 shrink-0">
                    Dill User
                  </span>
                )}
                {!salesRep.userId && (
                  <span className="text-[10px] font-medium text-gray-700 bg-gray-50 rounded-full px-2 py-0.5 shrink-0">
                    No Dill Access
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 m-0 truncate">
                {[salesRep.email, salesRep.phone].filter(Boolean).join(' · ') || 'No contact info'}
              </p>
            </div>

            <div className="flex items-center gap-2 shrink-0 pl-3 border-l border-gray-100">
              <DillButton
                variant="icon-default"
                icon={<Edit2 size={12} />}
                onClick={() => handleEdit(salesRep)}
              />
              <DillButton
                variant="icon-red"
                icon={<Trash2 size={12} />}
                onClick={() => handleDelete(salesRep.id)}
              />
            </div>
          </div>
        ))}
      </div>

      <SalesRepFormModal
        open={isFormModalOpen}
        onClose={handleClose}
        onSubmit={handleSubmit}
        initialData={editingSalesRep}
        isLoading={loadingList.includes('createSalesRep') || loadingList.includes('updateSalesRep')}
        errorMessage={errorMessage}
        supplierUsers={supplierUsers}
        existingSalesReps={salesReps}
      />
    </div>
  );
};

export default SalesRepsSettingsSection;
